import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Brain, 
  BarChart3, 
  Users, 
  TrendingUp, 
  MessageSquare, 
  Sparkles,
  ArrowRight,
  ChevronRight,
  Zap,
  Shield,
  Globe,
  LineChart,
  PieChart,
  Target,
  Layers,
  Moon,
  Sun
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const FUNDLE_LOGO = 'https://customer-assets.emergentagent.com/job_fundle-ai/artifacts/tu7vfzht_erj2rwul_fundle_logo-white-min-382x100.png';

export default function Landing() {
  const navigate = useNavigate();
  const { isAuthenticated, checkAuth } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const check = async () => {
      // If we just logged out, don't auto-check auth
      const justLoggedOut = sessionStorage.getItem('just_logged_out');
      if (justLoggedOut === 'true') {
        sessionStorage.removeItem('just_logged_out');
        setIsLoading(false);
        return;
      }
      
      // Set a maximum loading time of 3 seconds
      const timeout = setTimeout(() => {
        setIsLoading(false);
      }, 3000);
      
      try {
        const user = await checkAuth();
        if (user) {
          navigate('/dashboard');
        }
      } catch (e) {
        console.log('Auth check failed:', e);
      } finally {
        clearTimeout(timeout);
        setIsLoading(false);
      }
    };
    check();
  }, [checkAuth, navigate]);

  const handleLogin = () => {
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Brain className="w-12 h-12 text-primary animate-pulse" />
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden" data-testid="landing-page">
      {/* Animated background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-secondary/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-accent/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.3)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border)/0.3)_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      </div>

      {/* Navigation */}
      <nav className="relative z-10 flex items-center justify-between px-6 lg:px-12 py-4 border-b border-border/50 backdrop-blur-sm bg-background/80">
        <div className="flex items-center gap-3">
          <Brain className="w-8 h-8 text-primary" />
          <span className="text-xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            Fundle Brain
          </span>
        </div>
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="rounded-full"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
          <Button 
            variant="outline"
            onClick={handleLogin} 
            className="gap-2"
            data-testid="nav-login-btn"
          >
            Enterprise Login
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 px-6 lg:px-12 pt-20 pb-32">
        <div className="max-w-6xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-sm mb-8">
            <Sparkles className="w-4 h-4 text-primary" />
            <span>AI-Powered Analytics Engine</span>
          </div>

          {/* Main headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight mb-6">
            <span className="bg-gradient-to-r from-foreground via-foreground to-muted-foreground bg-clip-text">
              Transform Your
            </span>
            <br />
            <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
              Loyalty Data Into Intelligence
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto mb-12">
            Fundle Brain is your AI-powered analytics companion. Get instant insights, 
            predictive analytics, and actionable recommendations from your loyalty program data.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Button 
              onClick={handleLogin} 
              size="lg" 
              className="gap-2 px-8 h-14 text-lg bg-gradient-to-r from-primary to-secondary hover:opacity-90"
              data-testid="hero-login-btn"
            >
              <Brain className="w-5 h-5" />
              Get Started Free
            </Button>
            <Button variant="outline" size="lg" className="gap-2 px-8 h-14 text-lg">
              Watch Demo
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { value: '2.4M+', label: 'Customers Analyzed' },
              { value: '500M+', label: 'Revenue Tracked' },
              { value: '50+', label: 'Store Locations' },
              { value: '99.9%', label: 'Uptime SLA' },
            ].map((stat, idx) => (
              <div key={idx} className="p-4 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50">
                <div className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  {stat.value}
                </div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative z-10 px-6 lg:px-12 py-24 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              AI-Powered Analytics Suite
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Everything you need to understand, engage, and grow your loyalty program
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: BarChart3,
                title: 'AI Dashboards',
                description: 'Real-time dashboards with AI-generated insights and anomaly detection',
                color: 'text-primary',
                bg: 'bg-primary/10'
              },
              {
                icon: LineChart,
                title: 'Trend Analysis',
                description: 'Predictive trends and forecasting powered by machine learning',
                color: 'text-secondary',
                bg: 'bg-secondary/10'
              },
              {
                icon: Users,
                title: 'RFM Segmentation',
                description: 'Automatic customer segmentation with Champions, Loyal, At-Risk groups',
                color: 'text-accent',
                bg: 'bg-accent/10'
              },
              {
                icon: Target,
                title: 'Cohort Analysis',
                description: 'VN-style cohorts tracking one-timers vs repeat customers',
                color: 'text-yellow-500',
                bg: 'bg-yellow-500/10'
              },
              {
                icon: PieChart,
                title: 'Store Performance',
                description: 'Compare stores, track metrics, identify top and under performers',
                color: 'text-blue-500',
                bg: 'bg-blue-500/10'
              },
              {
                icon: MessageSquare,
                title: 'AI Conversation',
                description: 'Chat with your data using natural language powered by GPT',
                color: 'text-green-500',
                bg: 'bg-green-500/10'
              },
            ].map((feature, idx) => (
              <div 
                key={idx} 
                className="p-6 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all duration-300 group"
              >
                <div className={`w-12 h-12 rounded-xl ${feature.bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <feature.icon className={`w-6 h-6 ${feature.color}`} />
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="relative z-10 px-6 lg:px-12 py-24">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              How Fundle Brain Works
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Connect your data and let AI do the heavy lifting
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                icon: Globe,
                title: 'Connect Your Data',
                description: 'Securely connect via our Data Bridge API or upload your loyalty data'
              },
              {
                step: '02',
                icon: Brain,
                title: 'AI Analyzes',
                description: 'Our AI engine processes millions of records in seconds'
              },
              {
                step: '03',
                icon: Zap,
                title: 'Get Insights',
                description: 'Receive actionable insights, reports, and recommendations'
              },
            ].map((item, idx) => (
              <div key={idx} className="relative">
                <div className="text-6xl font-bold text-muted/20 absolute -top-4 -left-2">
                  {item.step}
                </div>
                <div className="relative pt-8">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mb-4">
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="relative z-10 px-6 lg:px-12 py-24 bg-muted/30">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Shield className="w-6 h-6 text-primary" />
            <span className="text-lg font-semibold">Enterprise-Grade Security</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Your Data, Your Control
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            SOC 2 compliant infrastructure with end-to-end encryption. 
            Your loyalty data never leaves your secure environment.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 text-muted-foreground">
            {['AES-256 Encryption', 'Multi-tenant Isolation', 'GDPR Compliant', 'SSO Support'].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-accent" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 px-6 lg:px-12 py-24">
        <div className="max-w-4xl mx-auto">
          <div className="relative p-8 sm:p-12 rounded-3xl bg-gradient-to-br from-primary/20 via-secondary/10 to-accent/20 border border-border/50 overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-secondary/20 rounded-full blur-3xl" />
            
            <div className="relative text-center">
              <Brain className="w-16 h-16 mx-auto mb-6 text-primary" />
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Ready to Unlock Your Data&apos;s Potential?
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
                Join leading brands using Fundle Brain to transform their loyalty programs
              </p>
              <Button 
                onClick={handleLogin} 
                size="lg" 
                className="gap-2 px-8 h-14 text-lg"
                data-testid="cta-login-btn"
              >
                Start Free Trial
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-6 lg:px-12 py-12 border-t border-border/50">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Brain className="w-6 h-6 text-primary" />
            <span className="font-semibold">Fundle Brain</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2026 Fundle. AI-Powered Loyalty Analytics.
          </p>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <a href="#" className="hover:text-foreground transition-colors">Privacy</a>
            <a href="#" className="hover:text-foreground transition-colors">Terms</a>
            <a href="#" className="hover:text-foreground transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
